import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, MapPin, ChevronRight, Star, CalendarClock, User, Home } from "lucide-react";
import { toast } from "sonner";

export default function WorkerParticipants() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.workerParticipants()
      .then(setItems)
      .catch(() => toast.error("Could not load participants."))
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return <div className="flex justify-center pt-24"><Loader2 className="h-6 w-6 animate-spin text-[#00c86e]" /></div>;

  return (
    <div className="px-4 pt-5" data-testid="worker-participants-page">
      <h1 className="text-2xl font-bold text-white tracking-tight font-brand">My Participants</h1>
      <p className="text-xs text-slate-400 mt-1 mb-5">People you support or are rostered with</p>

      {items.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-slate-400 text-sm" data-testid="worker-no-participants">
          No participants to show right now.
        </div>
      )}

      <div className="space-y-2.5">
        {items.map((p) => (
          <button key={p.name} onClick={() => nav(`/worker/participant/${p.name}`)}
            data-testid={`worker-participant-${p.name}`}
            className="w-full text-left rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 active:scale-[0.99] transition-all p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
              <User className="h-5 w-5 text-[#00c86e]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-[15px] font-semibold truncate">{p.display_name}</div>
              {p.primary_address && (
                <div className="flex items-center gap-1 text-[11px] text-slate-400 truncate">
                  <MapPin className="h-3 w-3 shrink-0" /> {p.primary_address}
                </div>
              )}
              <div className="flex items-center gap-2 mt-1.5">
                {p.is_preferred && (
                  <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-full">
                    <Star className="h-2.5 w-2.5" /> Preferred
                  </span>
                )}
                {p.is_rostered && (
                  <span className="flex items-center gap-1 text-[10px] font-semibold text-[#00c86e] bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 rounded-full">
                    <CalendarClock className="h-2.5 w-2.5" /> Rostered
                  </span>
                )}
                {p.is_sil_preferred && (
                  <span className="flex items-center gap-1 text-[10px] font-semibold text-sky-300 bg-sky-500/15 border border-sky-500/30 px-2 py-0.5 rounded-full">
                    <Home className="h-2.5 w-2.5" /> SIL team
                  </span>
                )}
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}
